import React from 'react';
import { createPortal } from 'react-dom';
import { TooltipProps, DeviationTooltipContent, BaselineTooltipContent } from './types';

const Tooltip: React.FC<TooltipProps> = ({ tooltip }) => {
  if (!tooltip.visible || !tooltip.content) {
    return null;
  }

  // Check if this is deviation content, baseline content, or model disagreement content
  const isDeviationContent = (content: any): content is DeviationTooltipContent => {
    return content && 'deviation' in content && 'actualValue' in content && 'meanValue' in content;
  };

  const isBaselineContent = (content: any): content is BaselineTooltipContent => {
    return (
      content && 'difference' in content && 'baselineValue' in content && 'baselineLabel' in content
    );
  };

  // Use portal to render tooltip at document body level to escape card boundaries
  return createPortal(
    <div
      className="fixed z-[9999] px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm rounded-lg shadow-xl border border-gray-200 dark:border-gray-500 pointer-events-none"
      style={{
        left: tooltip.x,
        top: tooltip.y,
        transform: 'translate(-50%, -100%)',
        maxWidth: '280px',
        minWidth: '200px',
      }}
    >
      {isDeviationContent(tooltip.content) ? (
        // Deviation content
        <>
          <div className="font-medium mb-1">{tooltip.content.date}</div>
          <div className="space-y-1">
            <div className="text-gray-600 dark:text-gray-400">{tooltip.content.modelLabel}</div>
            <div className="flex justify-between gap-3">
              <span>Deviation:</span>
              <span
                className={`font-medium ${
                  tooltip.content.deviation > 0
                    ? 'text-red-500 dark:text-red-400'
                    : tooltip.content.deviation < 0
                      ? 'text-blue-500 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {tooltip.content.deviation > 0 ? '+' : ''}
                {tooltip.content.deviation.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span>Selected Run:</span>
              <span className="font-medium">{tooltip.content.actualValue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span>Mean:</span>
              <span className="font-medium">{tooltip.content.meanValue.toFixed(2)}</span>
            </div>
          </div>
        </>
      ) : isBaselineContent(tooltip.content) ? (
        // Baseline comparison content
        <>
          <div className="font-medium mb-1">{tooltip.content.timestep}</div>
          <div className="space-y-1">
            <div className="text-gray-600 dark:text-gray-400">{tooltip.content.modelLabel}</div>
            <div className="text-xs text-gray-500 dark:text-gray-500 mb-2">
              vs {tooltip.content.baselineLabel}
            </div>
            <div className="flex justify-between gap-3">
              <span>Difference:</span>
              <span
                className={`font-medium ${
                  tooltip.content.difference > 0
                    ? 'text-red-500 dark:text-red-400'
                    : tooltip.content.difference < 0
                      ? 'text-blue-500 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400'
                }`}
              >
                {tooltip.content.difference > 0 ? '+' : ''}
                {tooltip.content.difference.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between gap-3">
              <span>Selected Run:</span>
              <span className="font-medium">{tooltip.content.actualValue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span>Baseline:</span>
              <span className="font-medium">{tooltip.content.baselineValue.toFixed(2)}</span>
            </div>
          </div>
        </>
      ) : (
        // Original model disagreement content
        <>
          <div className="font-medium mb-1">{tooltip.content.date}</div>
          {tooltip.content.models.map((model, index) => (
            <div key={index} className="flex justify-between gap-3">
              <span>{model.name}</span>
              <span className="font-medium">{model.value}</span>
            </div>
          ))}
        </>
      )}
    </div>,
    document.body,
  );
};

export default Tooltip;
